// ─── Public Status Page — icon registry ──────────────────────────────
//
// WHY this exists instead of the app's <Icon> (apps/web/src/lib/icons.tsx):
// that component is a THEME-AWARE registry — it calls `useThemeStore` from
// lib/stores.ts, and lib/stores.ts imports lib/api.ts, the entire ~4,500-line
// authenticated API client (auth store, 401→lock bridge, every DTO helper).
// Importing one icon would therefore pull the whole signed-in app into the one
// page whose job is to render when the API and the database are DOWN. On top of
// that it bundles lib/iconData.ts — ~49 kB of SVG bodies for FOUR icon families
// (lucide / phosphor / tabler / heroicons), of which the public page can only
// ever use one, because it has no theme switcher.
//
// The app's default icon family is `lucide`, so the glyphs below are the SAME
// glyphs the app draws — direct, tree-shaken imports of the exact 17 icons this
// page uses, and nothing else. (The public page's pre-paint stamp sets
// data-theme="cb", which exists only to keep index.css's palette blocks from
// matching; it has no icon-family meaning.)
//
// One glyph per LEVEL, and that is a requirement rather than a nicety: status
// is never encoded by hue alone here. operational/degraded/maintenance/major/
// unknown map to five visibly different shapes (check-circle, triangle,
// wrench, octagon, dashed circle), so the page still reads for the ~8% of men
// with a red/green deficiency. Do not collapse two levels onto one glyph.

import type { CSSProperties } from 'react';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  CircleDashed,
  CircleQuestionMark,
  Clock,
  ExternalLink,
  Info,
  LoaderCircle,
  Mail,
  OctagonAlert,
  Phone,
  Search,
  TriangleAlert,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';

/** Semantic names — never a glyph name, so a swap is a one-line change here. */
export type SIconName =
  | 'operational'
  | 'degraded'
  | 'partial'
  | 'major'
  | 'maintenance'
  | 'unknown'
  | 'info'
  | 'external'
  | 'chevronRight'
  | 'chevronLeft'
  | 'mail'
  | 'phone'
  | 'search'
  | 'activity'
  | 'clock'
  | 'x'
  | 'spinner';

const REGISTRY: Record<SIconName, LucideIcon> = {
  operational: CircleCheckBig,
  degraded: TriangleAlert,
  partial: CircleAlert,
  major: OctagonAlert,
  maintenance: Wrench,
  unknown: CircleDashed,
  info: Info,
  external: ExternalLink,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  mail: Mail,
  phone: Phone,
  search: Search,
  activity: Activity,
  clock: Clock,
  x: X,
  spinner: LoaderCircle,
};

/** Last-resort glyph — a name can only be wrong if the registry is edited badly. */
const FALLBACK: LucideIcon = CircleQuestionMark;

export interface SIconProps {
  name: SIconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  /**
   * Accessible name. Omit for decoration next to real text (the icon is then
   * aria-hidden); pass it when the icon IS the only label (e.g. an icon-only
   * button), which promotes the svg to role="img".
   */
  title?: string;
}

export function SIcon({ name, size = 18, strokeWidth = 2, className, style, title }: SIconProps) {
  const Glyph = REGISTRY[name] ?? FALLBACK;
  // index.css ships a global `img, svg { display: block }` reset, which would
  // push an icon onto its own line beside text in any non-flex context. Force
  // inline-block + baseline-ish alignment + no shrink, exactly the fix
  // lib/icons.tsx applies for the same reason. Caller `style` wins.
  const iconStyle: CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    flexShrink: 0,
    ...style,
  };
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      style={iconStyle}
      focusable="false"
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
    />
  );
}
